// src/lib/authHelpers.ts
import { supabase } from './supabase'
import { UserProfile } from '../types'

// Types for JWT claims
export interface JWTClaims {
  role: 'super_admin' | 'admin' | 'sales_user' | 'market_access_user'
  organization_id: string | null
}

/**
 * Authentication helper functions for JWT claims management
 */
export const authHelpers = {
  /**
   * Initialize user claims after authentication
   * Call this after successful login or when user data changes
   */
  async initializeUserClaims(userId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('set_user_claims', {
        user_id: userId,
      })

      if (error) {
        console.error('Failed to set user claims:', error)
        throw error
      }

      // Force refresh the session to get updated claims
      await this.refreshSession()
    } catch (error) {
      console.error('Error initializing user claims:', error)
      throw error
    }
  },

  /**
   * Get user claims from JWT
   */
  async getUserClaims(): Promise<JWTClaims | null> {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session?.user) {
        return null
      }

      const claims = session.user.app_metadata

      return {
        role: claims?.role || 'sales_user',
        organization_id: claims?.organization_id || null,
      }
    } catch (error) {
      console.error('Error getting user claims:', error)
      return null
    }
  },

  /**
   * Refresh the current session to get updated claims
   */
  async refreshSession(): Promise<void> {
    try {
      const { error } = await supabase.auth.refreshSession()
      if (error) {
        console.error('Failed to refresh session:', error)
        throw error
      }
    } catch (error) {
      console.error('Error refreshing session:', error)
      throw error
    }
  },

  /**
   * Get user profile with fresh claims from database
   */
  async getUserProfileWithClaims(): Promise<UserProfile | null> {
    try {
      const { data: claims, error } = await supabase.rpc('get_user_claims')

      if (error) {
        console.error('Failed to get user claims:', error)
        return null
      }

      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (!user) {
        return null
      }

      // Fetch full profile from user_profiles table to get avatar_url and phone
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('avatar_url, phone, status, created_at, updated_at')
        .eq('id', user.id)
        .single()

      if (profileError) {
        console.warn('Failed to fetch full profile:', profileError)
      }

      return {
        id: user.id,
        email: user.email!,
        role: (claims && typeof claims === 'object' && 'role' in claims
          ? claims.role
          : 'sales_user') as
          | 'super_admin'
          | 'admin'
          | 'sales_user'
          | 'market_access_user',
        organization_id: (claims &&
        typeof claims === 'object' &&
        'organization_id' in claims
          ? claims.organization_id
          : undefined) as string | undefined,
        first_name: (claims &&
        typeof claims === 'object' &&
        'first_name' in claims
          ? claims.first_name
          : undefined) as string | undefined,
        last_name: (claims &&
        typeof claims === 'object' &&
        'last_name' in claims
          ? claims.last_name
          : undefined) as string | undefined,
        avatar_url: profile?.avatar_url || undefined,
        phone: profile?.phone || undefined,
        status: profile?.status || 'active',
        created_at: profile?.created_at || user.created_at,
        updated_at: profile?.updated_at || user.updated_at || user.created_at,
      }
    } catch (error) {
      console.error('Error getting user profile with claims:', error)
      return null
    }
  },
}
